import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppLogo } from './hooks/useAppLogo';
import { API_BASE_URL } from './utils/apiConfig';
import './styles-super-admin-login.css';

function SuperAdminLogin() {
  const [form, setForm] = useState({ username: '', password: '', rememberMe: false });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logoUrl } = useAppLogo();

  useEffect(() => {
    const usernameInput = document.getElementById('username');
    if (usernameInput) usernameInput.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const loginData = {
        username: form.username,
        password: form.password,
        role: 'SUPER_ADMIN'
      };
      const loginUrl = `${API_BASE_URL}/api/auth/login`;
      console.log('🔐 Login attempt:', {
        url: loginUrl,
        apiBaseUrl: API_BASE_URL,
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        username: form.username
      });
      const res = await axios.post(loginUrl, loginData, {
        timeout: 5000, // 5 second timeout
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Check if response is HTML instead of JSON (indicates routing/proxy issue)
      const contentType = res.headers['content-type'] || '';
      const responseText = typeof res.data === 'string' ? res.data : JSON.stringify(res.data);
      const isHtmlResponse = contentType.includes('text/html') || 
                            (typeof res.data === 'string' && res.data.trim().startsWith('<!doctype html'));
      
      if (isHtmlResponse) {
        console.error('❌ Received HTML instead of JSON response:', {
          contentType,
          url: loginUrl,
          apiBaseUrl: API_BASE_URL,
          responsePreview: responseText.substring(0, 200)
        });
        throw new Error('Server configuration error: API endpoint returned HTML instead of JSON. Please check reverse proxy settings.');
      }
      
      // Verify response has required data
      if (!res.data.token || !res.data.user) {
        console.error('❌ Invalid response structure:', {
          hasToken: !!res.data.token,
          hasUser: !!res.data.user,
          responseData: res.data,
          status: res.status,
          headers: res.headers
        });
        throw new Error('Invalid response from server');
      }
      
      // Store authentication data
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      
      // Verify localStorage was set correctly
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      if (!storedToken || !storedUser) {
        throw new Error('Failed to store authentication data');
      }
      
      console.log('Login successful, user role:', res.data.user.role);
      
      if (form.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }
      toast.success(t('Welcome, Super Admin.'));
      
      // Navigate to dashboard
      const redirectPath = res.data.redirectPath || '/dashboard/superadmin';
      console.log('Redirecting to:', redirectPath);
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 1000);
    } catch (err) {
      // Log detailed error information
      const errorDetails = {
        message: err.message,
        code: err.code,
        url: `${API_BASE_URL}/api/auth/login`,
        apiBaseUrl: API_BASE_URL
      };
      
      // Only include response data if it's an axios error
      if (err.response) {
        errorDetails.response = err.response.data;
        errorDetails.status = err.response.status;
        errorDetails.statusText = err.response.statusText;
      } else if (err.request) {
        // Request was made but no response received
        errorDetails.request = 'No response received from server';
        errorDetails.isNetworkError = true;
      }
      
      console.error('❌ Login error:', errorDetails);
      
      // Check if this is a network/connection error (backend not running)
      const isNetworkError = 
        err.code === 'ERR_NETWORK' || 
        err.code === 'ECONNREFUSED' ||
        err.code === 'ETIMEDOUT' ||
        err.code === 'ECONNABORTED' ||
        err.message === 'Network Error' ||
        (err.message && err.message.includes('ERR_CONNECTION_REFUSED')) ||
        (err.message && err.message.includes('timeout'));
      
      let errorMessage;
      
      // Check if response is HTML (routing/proxy issue)
      const contentType = err.response?.headers?.['content-type'] || '';
      const responseData = err.response?.data;
      const responseStatus = err.response?.status;
      const isHtmlResponse = contentType.includes('text/html') || 
                            (typeof responseData === 'string' && responseData.trim().startsWith('<!doctype html'));
      
      if (isHtmlResponse) {
        // Extract title from HTML if possible for better diagnostics
        let htmlTitle = 'Unknown error';
        if (typeof responseData === 'string') {
          const titleMatch = responseData.match(/<title[^>]*>([^<]+)<\/title>/i);
          if (titleMatch) htmlTitle = titleMatch[1];
          
          // Check for common error patterns
          if (responseData.includes('404') || responseStatus === 404) {
            htmlTitle = '404 Not Found';
          } else if (responseData.includes('502') || responseStatus === 502) {
            htmlTitle = '502 Bad Gateway (Backend not reachable)';
          } else if (responseData.includes('503') || responseStatus === 503) {
            htmlTitle = '503 Service Unavailable';
          } else if (responseData.includes('500') || responseStatus === 500) {
            htmlTitle = '500 Internal Server Error';
          }
        }
        
        errorMessage = `Server Configuration Error: API returned HTML (${htmlTitle}) instead of JSON. This indicates the reverse proxy (nginx) is not routing /api/* requests to the backend server. Status: ${responseStatus || 'N/A'}`;
        
        console.error('❌ HTML response detected (Reverse Proxy Issue):', {
          url: `${API_BASE_URL}/api/auth/login`,
          status: responseStatus,
          contentType,
          htmlTitle,
          requestHeaders: err.config?.headers,
          responsePreview: typeof responseData === 'string' ? responseData.substring(0, 300) : 'N/A',
          troubleshooting: {
            issue: 'The reverse proxy (nginx) is likely not configured to proxy /api/* requests to the backend',
            check: [
              '1. Verify nginx has a location /api block that proxies to http://localhost:5000',
              '2. Ensure backend server is running on port 5000',
              '3. Check nginx error logs: sudo tail -f /var/log/nginx/error.log',
              '4. Verify backend is accessible: curl http://localhost:5000/api/auth/login',
              '5. Reload nginx after config changes: sudo nginx -t && sudo systemctl reload nginx'
            ]
          }
        });
      } else if (err.response?.data?.message) {
        // If we got a response from the server, show the server's error message
        errorMessage = err.response.data.message;
        // Add API URL info for debugging
        if (err.response.status === 401) {
          console.warn('⚠️ Authentication failed. Check:', {
            'API URL': `${API_BASE_URL}/api/auth/login`,
            'Username provided': form.username,
            'Server response': err.response.data.message,
            'Note': 'Verify the API URL is correct and user exists in production database'
          });
        }
      } else if (isNetworkError) {
        errorMessage = 'Unable to connect to server. Please ensure the backend server is running.';
        console.error('🌐 Network error - Backend may be unreachable at:', `${API_BASE_URL}/api/auth/login`);
      } else {
        errorMessage = err.response?.data?.message || t('Incorrect username or password.');
      }
      
      setMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="super-admin-login">
      {/* Animated Background */}
      <div className="sal-background">
        <div className="sal-gradient-orb sal-orb-1"></div>
        <div className="sal-gradient-orb sal-orb-2"></div>
        <div className="sal-gradient-orb sal-orb-3"></div>
        <div className="sal-grid-pattern"></div>
      </div>

      {/* Main Layout */}
      <div className="sal-layout">
        {/* Hero Section (Left/Bottom) */}
        <div className="sal-hero">
          <div className="sal-hero-content">
            <div className="sal-brand">
              {logoUrl ? (
                <img src={logoUrl} alt="PlayLive Pro Logo" className="sal-logo" />
              ) : (
                <img src="/logo192.png" alt="PlayLive Pro Logo" className="sal-logo" />
              )}
            </div>
            <h1 className="sal-title">PlayLive Pro</h1>
            <p className="sal-subtitle">TOURNAMENT MANAGEMENT SYSTEM</p>
            <div className="sal-divider"></div>
            <p className="sal-description">
              Streamline your tournaments with powerful tools for auction management, 
              team coordination, and real-time analytics.
            </p>
            <div className="sal-features">
              <span className="sal-feature">⚡ Live Auctions</span>
              <span className="sal-feature">📊 Analytics</span>
              <span className="sal-feature">🏆 Tournaments</span>
            </div>
          </div>
          <div className="sal-footer">
            <p>© 2025 PlayLive Pro. {t('All rights reserved.')}</p>
          </div>
        </div>

        {/* Login Section (Top Right) */}
        <div className="sal-login-area">
          <div className="sal-login-card">
            <div className="sal-card-header">
              <div className="sal-admin-badge">
                <span>🛡️</span>
                <span>SUPER ADMIN</span>
              </div>
              <h2>Welcome Back</h2>
              <p>Sign in to access the control center</p>
            </div>

            <form onSubmit={handleSubmit} className="sal-form">
              <div className="sal-fields-row">
                <div className="sal-field">
                  <label htmlFor="username">👤 Username</label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    placeholder={t('Enter your username')}
                    onChange={handleChange}
                    required
                    autoComplete="username"
                  />
                </div>

                <div className="sal-field">
                  <label htmlFor="password">🔐 Password</label>
                  <div className="sal-password-wrap">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('Enter your password')}
                      onChange={handleChange}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="sal-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
              </div>

              <div className="sal-options">
                <label className="sal-checkbox">
                  <input
                    name="rememberMe"
                    type="checkbox"
                    checked={form.rememberMe}
                    onChange={handleChange}
                  />
                  <span className="sal-checkmark"></span>
                  <span>{t('Keep me signed in')}</span>
                </label>
                <button type="button" onClick={handleForgotPassword} className="sal-forgot">
                  {t('Forgot Password?')}
                </button>
              </div>

              {message && (
                <div className="sal-error">
                  <span>⚠️</span>
                  <span>{message}</span>
                </div>
              )}

              <button type="submit" className="sal-submit" disabled={loading}>
                {loading ? (
                  <>
                    <span className="sal-spinner"></span>
                    <span>Authenticating...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <span className="sal-arrow">→</span>
                  </>
                )}
              </button>

              <div className="sal-security">
                <span>🔒</span>
                <span>Secured with 256-bit encryption</span>
              </div>
            </form>
          </div>

          <div className="sal-support">
            <span>{t('Need help?')}</span>
            <a href="mailto:playlive@gmail.com">📧 Email</a>
            <a href="https://wa.me/91XXXXXXXXXX" target="_blank" rel="noopener noreferrer">💬 WhatsApp</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuperAdminLogin;
